import React, { memo, useCallback, useContext, useMemo, useState } from 'react'
import { MenuActionItem } from 'src/interfaces/ui'
import { ActionMenu } from 'src/components/shared/ActionMenu'
import { CancelMinor, ViewMinor } from '@shopify/polaris-icons'
import tw from 'twin.macro'
import { SensitiveActionModal } from 'src/components/shared/SensitiveActionModal'
import { createAsyncAction } from 'src/utils/reduxUtils'
import { useDispatch, useSelector } from 'react-redux'
import { CancelPayment, DeletePayment, FetchPaymentInvoice } from 'src/constants/actionTypes'
import { getPartnerId } from 'src/selectors/user'
import { showToast } from 'src/utils/toast'
import { PaymentStatus } from 'src/constants/payment/enums'
import { PaymentInvoiceModal } from '../../PaymentInvoiceModal'
import { UserPermission } from 'src/constants/user'
import { handleApiError } from 'src/utils/errorHandlers'
import useCheckPermissions from 'src/hooks/useCheckPermissions'
import { PaymentsTableContext } from '../../payments-table/PaymentsTable.context'
import { ReloadWrapperContext } from 'src/components/wrapper/reload-wrapper/ReloadWrapper.context'
import { useModalOpen } from 'src/hooks/useModalOpen'
import { colors } from 'src/theme'

const PaymentsItemActionMenuComponent = () => {
  const dispatch = useDispatch()

  const { reload } = useContext(ReloadWrapperContext)
  const { paymentToAction, actionAnchor, closePaymentsItemAction, clearPaymentsItemAction } =
    useContext(PaymentsTableContext)

  const partnerId = useSelector(getPartnerId)

  const { hasPermission } = useCheckPermissions()

  const {
    isOpen: isDeleteConfirmOpen,
    open: handleOpenDeleteConfirm,
    close: handleCloseDeleteConfirm,
  } = useModalOpen()

  const {
    isOpen: isCancelConfirmOpen,
    open: handleOpenCancelConfirm,
    close: handleCloseCancelConfirm,
  } = useModalOpen()

  const isOpen = Boolean(actionAnchor)

  const [invoice, setInvoice] = useState<any>(null)

  const clearInvoice = useCallback(() => {
    setInvoice(null)
  }, [])

  const handleDeletePayment = useCallback(async () => {
    if (!paymentToAction || !partnerId) {
      return
    }

    try {
      await createAsyncAction(
        dispatch,
        DeletePayment.request({
          partnerId,
          paymentIds: [paymentToAction.id],
        }),
      )

      reload()

      showToast('Payment is deleted.', { variant: 'success' })
    } catch (err) {
      handleApiError(err)
    }

    handleCloseDeleteConfirm()
  }, [paymentToAction, partnerId, reload])

  const handleCancelPayment = useCallback(async () => {
    if (!paymentToAction || !partnerId) {
      return
    }

    try {
      await createAsyncAction(
        dispatch,
        CancelPayment.request({
          paymentId: paymentToAction.id,
        }),
      )

      reload()

      showToast('Payment is canceled.', { variant: 'success' })
    } catch (err) {
      handleApiError(err)
    }

    handleCloseCancelConfirm()
  }, [paymentToAction, partnerId, reload])

  const handleDownloadInvoice = useCallback(async () => {}, [invoice])

  const actionItems: MenuActionItem[] = useMemo(() => {
    const res: any = []

    if (paymentToAction?.status === PaymentStatus.Paid) {
      res.push({
        label: 'View invoice',
        icon: <ViewMinor width={20} height={20} />,
        onClick: async () => {
          if (!paymentToAction) {
            return
          }

          const buffer: any = await createAsyncAction(
            dispatch,
            FetchPaymentInvoice.request({
              partnerId,
              paymentId: paymentToAction.id,
            }),
          )

          const blob = new Blob([buffer], { type: 'application/pdf' })

          setInvoice(URL.createObjectURL(blob))

          closePaymentsItemAction()
        },
      })
    }

    if (
      paymentToAction?.status &&
      [PaymentStatus.NotPaid, PaymentStatus.RequiresFunding].includes(paymentToAction.status) &&
      hasPermission(UserPermission.PaymentsDelete)
    ) {
      res.push({
        label: 'Delete Payment',
        icon: <CancelMinor width={20} height={20} fill={colors.COLORS_RED_500} />,
        color: colors.COLORS_RED_500,
        onClick: () => {
          if (!paymentToAction) {
            return
          }

          handleOpenDeleteConfirm()
          closePaymentsItemAction()
        },
      })
    }

    if (paymentToAction?.isCancelable && hasPermission(UserPermission.PaymentsEdit)) {
      res.push({
        label: 'Cancel Payment',
        icon: <CancelMinor width={20} height={20} fill={colors.COLORS_RED_500} />,
        color: colors.COLORS_RED_500,
        onClick: () => {
          if (!paymentToAction) {
            return
          }

          handleOpenCancelConfirm()
          closePaymentsItemAction()
        },
      })
    }

    return res
  }, [paymentToAction, hasPermission, handleOpenCancelConfirm, handleOpenDeleteConfirm])

  return (
    <>
      <ActionMenu
        anchorEl={actionAnchor}
        itemClassName={tw`min-w-[208px]`}
        isOpen={isOpen}
        items={actionItems}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        onClose={clearPaymentsItemAction}
      />
      <SensitiveActionModal
        isOpen={isDeleteConfirmOpen}
        title="Delete payment"
        text="Are you sure you want to delete payment?"
        submitLabel="Delete"
        onClose={handleCloseDeleteConfirm}
        onSubmit={handleDeletePayment}
      />
      <SensitiveActionModal
        isOpen={isCancelConfirmOpen}
        title="Cancel payment"
        text="Are you sure you want to cancel payment?"
        submitLabel="Cancel"
        onClose={handleCloseCancelConfirm}
        onSubmit={handleCancelPayment}
      />
      <PaymentInvoiceModal
        isOpen={Boolean(invoice)}
        invoice={invoice}
        onClose={clearInvoice}
        onDownload={handleDownloadInvoice}
      />
    </>
  )
}

export const PaymentsItemActionMenu = memo(PaymentsItemActionMenuComponent)
